// src/App.jsx
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// COMMON PAGES
import LoginForm from "./pages/LoginForm";
import RegistrationForm from "./pages/RegistrationForm";
import PrivateRoute from "./pages/PrivateRoute";
import RoleBasedRoute from "./pages/RoleBasedRoute";

// ADMIN PAGES
import AdminDashboard from "./pages/AdminDashboard";
import AdminUsers from "./pages/AdminUsers";
import AdminUserAdd from "./pages/AdminUserAdd";
import AdminUserUpdate from "./pages/AdminUserUpdate";
import AdminVehicles from "./pages/AdminVehicles";
import VehicleRegister from "./pages/VehicleRegister";
import VehicleUpdate from "./pages/VehicleUpdate";
import AdminBooking from "./pages/AdminBooking";
import AdminVehicleAssign from "./pages/AdminVehicleAssign";
import AdminReports from "./pages/AdminReports";
import AdminMap from "./pages/AdminMap"; // ⭐ NEW PAGE YOU REQUESTED
import AdminDepartment from "./pages/AdminDepartment"; // department page
import AdminWorkArea from "./pages/AdminWorkArea"; // work area page
import AdminBookingSwap from "./pages/AdminBookingSwap";

// EMPLOYEE PAGES
import EmployeeDashboard from "./pages/EmployeeDashboard";
import EmployeeNearbyVehicles from "./pages/EmployeeNearbyVehicles";
import EmployeeBooking from "./pages/EmployeeBooking";
import EmployeeMyBookings from "./pages/EmployeeMyBookings";
// import ScheduleBooking from "./pages/ScheduleBooking";
import ScheduleBooking from "./pages/ScheduleBookingNew";


// DRIVER PAGES
import DriverDashboard from "./pages/DriverDashboard";
import DriverTrip from "./pages/DriverTrip";
import DriverMyTrips from "./pages/DriverMyTrips";
import DriverScheduledBookings from "./pages/DriverScheduledBookings";

// TRACK
import TrackBooking from "./pages/TrackBooking";

// COMPONENTS - Assistive Touch Bubble for drivers
import AssistiveTouchBubble from "./components/AssistiveTouchBubble";
import BookingAssistBubble from "./components/BookingAssistBubble";
import EmployeeBookingAlert from "./components/EmployeeBookingAlert";
import AdminEmergencyAlert from "./components/AdminEmergencyAlert"; // ⭐ NEW

function App() {
  const { isAuthenticated, userDetails } = useSelector((state) => state.auth);
  const location = useLocation();
  const role = (userDetails?.role || "").toString().trim().toLowerCase();
  const hasRole = Boolean(userDetails?.role);
  const isDriver = role === "driver" || userDetails?.role === "D";
  const isAdmin = role === "admin" || role === "ad";
  const isEmployee = !isAdmin && !isDriver;
  const isAdminRoute = location.pathname.startsWith("/admin");

  return (
    <>
      <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
      {/* Global helpers gated by role/route to avoid unnecessary polling */}
      {hasRole && isDriver && <AssistiveTouchBubble />}
      {hasRole && isEmployee && <BookingAssistBubble />}
      {hasRole && isEmployee && <EmployeeBookingAlert />}
      {hasRole && isAdminRoute && isAdmin && <AdminEmergencyAlert />} {/* Admin alerts only on admin routes */}
      
      <Routes>
      {/* PUBLIC ROUTES */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <LoginForm />}
      />
      <Route
        path="/register"
        element={isAuthenticated ? <Navigate to="/dashboard" /> : <RegistrationForm />}
      />
      <Route path="/track" element={<TrackBooking />} />

      {/* AUTH + ROLE REDIRECT */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <RoleBasedRoute />
          </PrivateRoute>
        }
      />

      {/* ==================== ADMIN ROUTES ==================== */}
      <Route
        path="/admin/dashboard"
        element={
          <PrivateRoute>
            <AdminDashboard />
          </PrivateRoute>
        }
      />

      {/* ⭐ NEW MAP ROUTE YOU ASKED TO ADD */}
     <Route
  path="/admin/map"
  element={
    <PrivateRoute>
      <AdminMap />
    </PrivateRoute>
  }
/>

      {/* Department & Work Area routes */}
      <Route
        path="/admin/department"
        element={
          <PrivateRoute>
            <AdminDepartment />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/work-area"
        element={
          <PrivateRoute>
            <AdminWorkArea />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <PrivateRoute>
            <AdminUsers />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/add-user"
        element={
          <PrivateRoute>
            <AdminUserAdd />
          </PrivateRoute>
        }
      />

      <Route
        path="/admin/vehicles"
        element={
          <PrivateRoute>
            <AdminVehicles />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/vehicle-register"
        element={
          <PrivateRoute>
            <VehicleRegister />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/vehicle-update/:id"
        element={
          <PrivateRoute>
            <VehicleUpdate />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/bookings"
        element={
          <PrivateRoute>
            <AdminBooking />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/vehicle-assign"
        element={
          <PrivateRoute>
            <AdminVehicleAssign />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <PrivateRoute>
            <AdminReports />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/booking-swap"
        element={
          <PrivateRoute>
            <AdminBookingSwap />
          </PrivateRoute>
        }
      />

      {/* ==================== EMPLOYEE ROUTES ==================== */}
      <Route
        path="/employee/dashboard"
        element={
          <PrivateRoute>
            <EmployeeDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/employee/vehicles"
        element={
          <PrivateRoute>
            <EmployeeNearbyVehicles />
          </PrivateRoute>
        }
      />
      <Route
        path="/employee/book"
        element={
          <PrivateRoute>
            <EmployeeBooking />
          </PrivateRoute>
        }
      />
      <Route
        path="/employee/my-bookings"
        element={
          <PrivateRoute>
            <EmployeeMyBookings />
          </PrivateRoute>
        }
      />

      {/* Uncomment to this use again */}
      {/* <Route
        path="/employee/schedule-booking"
        element={
          <PrivateRoute>
            <ScheduleBooking />
          </PrivateRoute>
        }
      /> */}

      <Route
        path="/employee/schedule-booking"
        element={
          <PrivateRoute>
            <ScheduleBooking />
          </PrivateRoute>
        }
      />
      <Route
        path="/schedule-booking"
        element={
          <PrivateRoute>
            <ScheduleBooking />
          </PrivateRoute>
        }
      />

      {/* ==================== DRIVER ROUTES ==================== */}
      <Route
        path="/driver/dashboard"
        element={
          <PrivateRoute>
            <DriverDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/driver-trip/:bookingId"
        element={
          <PrivateRoute>
            <DriverTrip />
          </PrivateRoute>
        }
      />
      <Route
        path="/trip/:bookingId"
        element={
          <PrivateRoute>
            <DriverTrip />
          </PrivateRoute>
        }
      />
      <Route
        path="/driver-trip"
        element={
          <PrivateRoute>
            <DriverTrip />
          </PrivateRoute>
        }
      />
      <Route
        path="/driver/my-trips"
        element={
          <PrivateRoute>
            <DriverMyTrips />
          </PrivateRoute>
        }
      />
      <Route
        path="/driver/scheduled-bookings"
        element={
          <PrivateRoute>
            <DriverScheduledBookings />
          </PrivateRoute>
        }
      />

      {/* DEFAULT REDIRECTS */}
      <Route path="/" element={<Navigate to="/login" />} />
      <Route path="*" element={<Navigate to="/login" />} />
    </Routes>
    </>
  );
}

export default App;
